import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/utils/analytics";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, referralCode?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        trackEvent("signin_failed", { error: error.message });
        throw error;
      }

      trackEvent("signin_success", { userId: data?.user?.id });

      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });

      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      const msg = String(error?.message || "");
      const friendly =
        msg.toLowerCase().includes("failed to fetch")
          ? "Network error. Please check your internet and try again."
          : msg.toLowerCase().includes("invalid login credentials")
            ? "Incorrect email or password."
            : msg.toLowerCase().includes("email not confirmed")
              ? "Your email is not verified yet. Please check your inbox."
              : msg || "Sign-in failed. Please try again.";

      toast({
        title: "Sign-in failed",
        description: friendly,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    referralCode?: string
  ) => {
    try {
      let referrerId: string | undefined;

      if (referralCode) {
        const { data: referrerUserId } = await supabase
          .rpc("get_referrer_id_by_code", { _referral_code: referralCode.toUpperCase() });

        if (referrerUserId) {
          referrerId = referrerUserId;
        }
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            referred_by: referrerId,
          },
        },
      });

      if (error) throw error;

      trackEvent("signup_success", {
        userId: data?.user?.id,
        fullName,
        hasReferral: !!referralCode,
      });

      // If the backend requires email confirmation, there won't be a session yet.
      if (!data?.session) {
        toast({
          title: "Account created!",
          description: "Now sign in with your email and password to continue.",
        });
        navigate("/auth", { replace: true });
        return;
      }

      toast({
        title: "Welcome to VIVIDAD!",
        description: "Your account is ready.",
      });

      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      const msg = String(error?.message || "");
      const friendly =
        msg.toLowerCase().includes("user already registered")
          ? "This email is already registered. Please sign in instead."
          : msg || "Sign-up failed. Please try again.";

      toast({
        title: "Sign-up failed",
        description: friendly,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();

      toast({
        title: "Signed out",
        description: "You've been successfully signed out.",
      });
      navigate("/", { replace: true });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error signing in with Google",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
